import { useState, useEffect, useRef, useCallback } from 'react';
import { track } from '../utils/trackAction';

/**
 * Dock de burbujas flotantes.
 *
 * Todo lo que flota sobre el helpdesk (WhatsApp, tickets, chat, llamada, asistencia remota,
 * timer de proyecto, "ver como", campana de acciones, los botones flotantes de cada página)
 * pasa por acá. Cada elemento elige una zona y el dock calcula la posición de TODOS juntos,
 * para que nunca se pisen. Reglas (15-oct-2026, Andy: "fix docking of all the bubbles…
 * there's plenty of these bubbles"):
 *
 *  1. Área segura medida del DOM, no números fijos: en escritorio las burbujas de la
 *     izquierda arrancan después de la barra lateral (plegada o no) y las de arriba debajo
 *     del encabezado (y de los avisos de "viendo como" / "sin conexión"). Antes la zona
 *     izquierda caía en x=12, ENCIMA del menú, y la de arriba tapaba la campana.
 *  2. Orden estable: dentro de una zona se apilan por prioridad, no por orden de montaje.
 *     Antes, cuando aparecía la llamada o la asistencia remota, las demás cambiaban de lugar.
 *  3. Sin superposiciones: cada elemento busca el lugar libre más cercano a su ancla y,
 *     si su columna está llena, pasa a otra columna hacia el centro. Se compara contra
 *     todo lo ya colocado, también contra elementos de otras zonas.
 *  4. Lo que no se ve no ocupa lugar (montado sin dibujar, o con display:none).
 *  5. Mientras se arrastra se marca la zona destino (ver DockDropHint).
 */

const DRAG_THRESHOLD = 5;
const MARGIN = 12;
const GAP = 10;
const MOBILE_BREAKPOINT = 768;

const DESKTOP_ZONES = ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right'];
const MOBILE_ZONES = ['bottom-left', 'bottom-center', 'bottom-right'];

// Más chico = más cerca de la esquina. Lo que no está acá va al final, por orden de llegada.
const PRIORITY = {
  'page-fab': 0,              // botón flotante propio de la página (nuevo ticket en móvil)
  'kb-toggle': 1,             // atajos de teclado en el detalle de ticket
  'guided-tour': 2,
  'tk-bubble-pos': 10,
  'wa-bubble-pos': 20,
  'floating-chat-pos': 30,
  'agent-actions-bell': 35,
  'admin-viewas-pos': 40,
  'project-timer-pos': 50,
  'remote-assist-pos': 60,
  'voice-call-widget-pos': 70,
};

// ── Registro compartido ──
const occupants = new Map(); // key → { zone, w, h, seq }
const listeners = new Set();
let seqCounter = 0;
let layoutCache = null;
let dragState = null; // { key, zone } mientras se arrastra algo

function notify() {
  layoutCache = null;
  listeners.forEach((fn) => fn());
}

function setOccupant(key, zone, w, h) {
  const prev = occupants.get(key);
  if (prev && prev.zone === zone && Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1) return;
  occupants.set(key, { zone, w, h, seq: prev ? prev.seq : ++seqCounter });
  notify();
}

function removeOccupant(key) {
  if (occupants.delete(key)) notify();
}

function setDragState(next) {
  const same = (!next && !dragState) || (next && dragState && next.key === dragState.key && next.zone === dragState.zone);
  if (same) return;
  dragState = next;
  listeners.forEach((fn) => fn());
}

export function subscribeDock(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getDragState() {
  return dragState;
}

// ── Área segura ──
function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
}

function cssPx(name) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return parseInt(v, 10) || 0;
}

/**
 * Márgenes del viewport que las burbujas no deben tapar. Se mide el DOM real, así sigue
 * a la barra lateral plegada/desplegada y a los avisos que empujan el encabezado.
 */
export function getDockInsets() {
  if (typeof window === 'undefined') return { top: 0, right: 0, bottom: 0, left: 0 };
  const mobile = isMobileViewport();
  let top = 0;
  const header = document.querySelector('.app-layout .header');
  if (header) {
    const r = header.getBoundingClientRect();
    if (r.height > 0 && r.bottom > 0 && r.bottom < window.innerHeight / 2) top = r.bottom;
  }
  let left = 0;
  if (!mobile) {
    const sidebar = document.querySelector('.app-layout .sidebar');
    if (sidebar) {
      const r = sidebar.getBoundingClientRect();
      if (r.width > 0 && r.left <= 1 && r.right > 0 && r.right < window.innerWidth / 2) left = r.right;
    }
  }
  const bottom = cssPx('--mobile-bottom-nav') * (mobile ? 1 : 0) + cssPx('--safe-bottom');
  return { top, right: 0, bottom, left };
}

// Cuando la barra lateral se pliega o aparece un aviso arriba, el área segura cambia sin
// que cambie el tamaño de la ventana: se observan los dos elementos.
let observedSidebar = null;
let observedHeader = null;
let chromeObserver = null;
function watchChrome() {
  if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return;
  const sidebar = document.querySelector('.app-layout .sidebar');
  const header = document.querySelector('.app-layout .header');
  if (sidebar === observedSidebar && header === observedHeader) return;
  if (!chromeObserver) chromeObserver = new ResizeObserver(() => notify());
  chromeObserver.disconnect();
  observedSidebar = sidebar;
  observedHeader = header;
  if (sidebar) chromeObserver.observe(sidebar);
  if (header) chromeObserver.observe(header);
}

// ── Layout ──
function sortedItems(zone) {
  const list = [];
  occupants.forEach((o, key) => {
    if (o.zone === zone && o.w > 0 && o.h > 0) list.push({ key, ...o });
  });
  const prio = (k) => (k in PRIORITY ? PRIORITY[k] : 1000);
  list.sort((a, b) => prio(a.key) - prio(b.key) || a.seq - b.seq);
  return list;
}

// Orden de colocación: primero las esquinas (son las posiciones "naturales"), después el
// medio, que se acomoda en el hueco que quede.
const PLACEMENT_ORDER = ['bottom-right', 'bottom-left', 'bottom-center', 'top-right', 'top-left', 'middle-right', 'middle-left'];

const overlaps = (a, b) =>
  a.x < b.x + b.w + GAP && b.x < a.x + a.w + GAP && a.y < b.y + b.h + GAP && b.y < a.y + a.h + GAP;

/**
 * Coloca todo sin superposiciones. Cada elemento busca, en su columna, el lugar libre más
 * cercano a su ancla (abajo, arriba o centro de la zona); si en esa columna no entra, pasa
 * a la siguiente hacia el centro de la pantalla. Se compara contra TODO lo ya colocado,
 * así que tampoco choca con elementos de otras zonas (un widget ancho de una esquina contra
 * una burbuja de la otra, en el celular).
 */
function computeLayout() {
  watchChrome();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const safe = getDockInsets();
  const minY = safe.top + MARGIN;
  const maxY = vh - safe.bottom - MARGIN;
  const leftX = safe.left + MARGIN;
  const rightX = vw - safe.right - MARGIN;
  const mobile = isMobileViewport();
  const placed = [];
  const out = {};

  const zones = mobile ? MOBILE_ZONES : DESKTOP_ZONES;
  for (const zone of PLACEMENT_ORDER) {
    if (!zones.includes(zone)) continue;
    const [v, edge] = zone.split('-');
    for (const it of sortedItems(zone)) {
      const { w, h } = it;
      const anchorY = v === 'top' ? minY : v === 'middle' ? Math.round((minY + maxY) / 2 - h / 2) : maxY - h;
      let offset = 0;
      let spot = null;
      const triedX = [];
      for (let attempt = 0; attempt < 8 && !spot; attempt++) {
        let x;
        if (edge === 'left') x = leftX + offset;
        else if (edge === 'right') x = rightX - offset - w;
        else x = Math.round(vw / 2 - w / 2) + (attempt % 2 ? -1 : 1) * offset;
        if (x < leftX - 0.5 || x + w > rightX + 0.5) break; // no hay más columnas
        triedX.push(x);
        // Candidatos: el ancla y los bordes de lo que ya está en esta columna.
        const inCol = placed.filter((r) => x < r.x + r.w + GAP && r.x < x + w + GAP);
        const cands = [anchorY];
        inCol.forEach((r) => { cands.push(r.y - GAP - h, r.y + r.h + GAP); });
        const free = cands
          .filter((y) => y >= minY - 0.5 && y + h <= maxY + 0.5)
          .filter((y) => !inCol.some((r) => overlaps({ x, y, w, h }, r)))
          // En las zonas de abajo sólo se sube y en las de arriba sólo se baja: así la
          // pila crece siempre hacia el mismo lado y no "salta" por encima del ancla.
          .filter((y) => (v === 'bottom' ? y <= anchorY + 0.5 : v === 'top' ? y >= anchorY - 0.5 : true))
          .sort((p, q) => Math.abs(p - anchorY) - Math.abs(q - anchorY));
        if (free.length) {
          spot = { x, y: free[0] };
        } else {
          // Próxima columna: más allá de lo más ancho que bloquea esta.
          const edgeDist = (r) => (edge === 'left' ? r.x + r.w - leftX : edge === 'right' ? rightX - r.x : Math.abs(r.x + r.w / 2 - vw / 2) + r.w / 2);
          const next = Math.max(offset, ...inCol.map(edgeDist)) + GAP;
          if (next <= offset) break;
          offset = next;
        }
      }
      // Pantalla llena (típico en el celular con la llamada y la asistencia abiertas a la
      // vez): no hay lugar sin encimar, así que va donde tape MENOS de lo ya colocado —
      // nunca encima de las burbujas de la esquina sólo por ser su ancla.
      if (!spot) {
        const xs = triedX.length ? triedX : [edge === 'left' ? leftX : edge === 'right' ? rightX - w : Math.round(vw / 2 - w / 2)];
        const ys = [anchorY, minY, maxY - h, ...placed.flatMap((r) => [r.y - GAP - h, r.y + r.h + GAP])]
          .map((y) => Math.max(minY, Math.min(maxY - h, y)));
        const area = (c) => placed.reduce((acc, r) => {
          const ox = Math.min(c.x + w, r.x + r.w) - Math.max(c.x, r.x);
          const oy = Math.min(c.y + h, r.y + r.h) - Math.max(c.y, r.y);
          // Se mide qué FRACCIÓN de cada vecino se tapa: tapar entera una burbuja de 52 px
          // la hace desaparecer; tapar un borde de un widget grande, no.
          return acc + (ox > 0 && oy > 0 ? (ox * oy) / (r.w * r.h) : 0);
        }, 0);
        let best = null;
        for (const x of xs) for (const y of ys) {
          const c = { x, y, a: area({ x, y }), d: Math.abs(y - anchorY) };
          if (!best || c.a < best.a || (c.a === best.a && c.d < best.d)) best = c;
        }
        spot = { x: best.x, y: best.y };
      }
      spot.x = Math.max(0, Math.min(vw - w, Math.round(spot.x)));
      spot.y = Math.max(0, Math.min(vh - h, Math.round(spot.y)));
      placed.push({ ...spot, w, h });
      out[it.key] = spot;
    }
  }
  return out;
}

function getLayout() {
  if (!layoutCache) layoutCache = computeLayout();
  return layoutCache;
}

// Posición de respaldo para el primer render (antes de medir el elemento).
function fallbackPosition(zone, w, h) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const safe = getDockInsets();
  const left = safe.left + MARGIN;
  const right = vw - safe.right - MARGIN - w;
  const topY = safe.top + MARGIN;
  const bottomY = vh - safe.bottom - MARGIN - h;
  const [v, hz] = zone.split('-');
  const x = hz === 'left' ? left : hz === 'center' ? Math.round(vw / 2 - w / 2) : right;
  const y = v === 'top' ? topY : v === 'middle' ? Math.round(vh / 2 - h / 2) : bottomY;
  return { x: Math.max(0, x), y: Math.max(0, y) };
}

function findZoneForPoint(x, y) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (isMobileViewport()) {
    if (x < vw / 3) return 'bottom-left';
    if (x < (2 * vw) / 3) return 'bottom-center';
    return 'bottom-right';
  }
  const safe = getDockInsets();
  // El medio horizontal se toma sobre el área de contenido, no sobre la ventana entera.
  const horizontal = x < (safe.left + vw) / 2 ? 'left' : 'right';
  const top = safe.top;
  const h = vh - top - safe.bottom;
  let vertical;
  if (y < top + h / 3) vertical = 'top';
  else if (y < top + (2 * h) / 3) vertical = 'middle';
  else vertical = 'bottom';
  return `${vertical}-${horizontal}`;
}

/** Rectángulo de una zona, para dibujar el destino mientras se arrastra. */
export function getZoneRect(zone) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const safe = getDockInsets();
  const left = safe.left;
  const top = safe.top;
  const w = vw - left - safe.right;
  const h = vh - top - safe.bottom;
  if (isMobileViewport()) {
    const i = MOBILE_ZONES.indexOf(zone);
    return { left: (vw / 3) * i, top: top + h * 0.55, width: vw / 3, height: h * 0.45 };
  }
  const [v, hz] = zone.split('-');
  const row = v === 'top' ? 0 : v === 'middle' ? 1 : 2;
  return {
    left: hz === 'left' ? left : left + w / 2,
    top: top + (h / 3) * row,
    width: w / 2,
    height: h / 3,
  };
}

function remapZoneForViewport(zone) {
  const valid = isMobileViewport() ? MOBILE_ZONES : DESKTOP_ZONES;
  if (valid.includes(zone)) return zone;
  if (zone && zone.endsWith('-left')) return 'bottom-left';
  if (zone && zone.endsWith('-right')) return 'bottom-right';
  return isMobileViewport() ? 'bottom-center' : 'bottom-right';
}

function readStoredZone(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const z = typeof parsed === 'string' ? parsed : parsed?.zone;
    if (z && [...DESKTOP_ZONES, ...MOBILE_ZONES].includes(z)) return z;
  } catch {}
  return null;
}

/**
 * Hook de anclaje por zonas.
 *
 * Devuelve un callback ref (`elementRef`) para que el dock mida el elemento, y la posición
 * calculada para TODOS los elementos a la vez (ver arriba).
 *
 * @param {string} storageKey  clave en localStorage para recordar la zona (y su id en el dock)
 * @param {object} [options]
 * @param {string}  [options.defaultZone='bottom-right']
 * @param {number}  [options.width=52]   tamaño estimado hasta poder medirlo
 * @param {number}  [options.height=52]
 * @param {boolean} [options.draggable=true]  false = ocupa lugar en el dock pero no se mueve
 *                                            (botones propios de una página)
 */
export default function useDraggable(storageKey, options = {}) {
  const defaultZone = options.defaultZone || 'bottom-right';
  const defaultW = options.width || 52;
  const defaultH = options.height || 52;
  const draggable = options.draggable !== false;

  const [zone, setZone] = useState(() => {
    const stored = draggable ? readStoredZone(storageKey) : null;
    return remapZoneForViewport(stored || defaultZone);
  });

  const [dragPos, setDragPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [, setTick] = useState(0);
  // ¿El componente llegó a dibujar algo? Los hooks no pueden ser condicionales, así que
  // varios de estos componentes llaman useDraggable y RECIÉN DESPUÉS hacen `return null`.
  // Si nadie llamó al callback ref con un nodo, no se dibujó y no ocupa lugar.
  const [attached, setAttached] = useState(false);

  const wasDragged = useRef(false);
  const dragStart = useRef(null);
  const dragPosRef = useRef(null);
  const sizeRef = useRef({ w: defaultW, h: defaultH });
  const measuredRef = useRef(false);
  const observerRef = useRef(null);
  const zoneRef = useRef(zone);
  zoneRef.current = zone;

  useEffect(() => subscribeDock(() => setTick((t) => t + 1)), []);

  // Cambio de tamaño de ventana: remapear zona (escritorio↔móvil) y recalcular todo.
  useEffect(() => {
    const handleResize = () => {
      setZone((prev) => remapZoneForViewport(prev));
      notify();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!draggable) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ zone }));
    } catch {}
  }, [zone, storageKey, draggable]);

  useEffect(() => {
    if (!attached) {
      removeOccupant(storageKey);
      return undefined;
    }
    setOccupant(storageKey, zone, sizeRef.current.w, sizeRef.current.h);
    return () => removeOccupant(storageKey);
  }, [zone, storageKey, attached]);

  const elementRef = useCallback(
    (el) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!el) { setAttached(false); return; }
      setAttached(true);
      const update = () => {
        const rect = el.getBoundingClientRect();
        // display:none (p. ej. con el teclado abierto en el celular) mide 0: no ocupa lugar.
        const w = rect.width;
        const h = rect.height;
        if (w > 0 && h > 0) {
          sizeRef.current = { w, h };
          measuredRef.current = true;
        }
        setOccupant(storageKey, zoneRef.current, w, h);
      };
      update();
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(update);
        ro.observe(el);
        observerRef.current = ro;
      }
    },
    [storageKey],
  );

  useEffect(() => () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // ── Arrastre ──
  const handleMove = useCallback((e) => {
    if (!dragStart.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragStart.current.clientX;
    const dy = clientY - dragStart.current.clientY;

    if (!dragStart.current.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      dragStart.current.moved = true;
      wasDragged.current = true;
      setIsDragging(true);
    }

    if (e.cancelable && e.touches) {
      try { e.preventDefault(); } catch {}
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { w, h } = sizeRef.current;
    const newPos = {
      x: Math.max(0, Math.min(vw - w, dragStart.current.startLeft + dx)),
      y: Math.max(0, Math.min(vh - h, dragStart.current.startTop + dy)),
    };
    dragPosRef.current = newPos;
    setDragPos(newPos);
    setDragState({ key: storageKey, zone: findZoneForPoint(newPos.x + w / 2, newPos.y + h / 2) });
  }, [storageKey]);

  const handleUp = useCallback(() => {
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', handleUp);
    window.removeEventListener('touchmove', handleMove);
    window.removeEventListener('touchend', handleUp);
    window.removeEventListener('touchcancel', handleUp);

    if (dragStart.current?.moved && dragPosRef.current) {
      const cx = dragPosRef.current.x + sizeRef.current.w / 2;
      const cy = dragPosRef.current.y + sizeRef.current.h / 2;
      const newZone = findZoneForPoint(cx, cy);
      if (newZone !== zoneRef.current) {
        // Instrumentar acá cubre todas las burbujas de una: comparten este hook.
        track('ui', 'bubble_drag', `${storageKey}:${newZone}`, { result: 'ok' });
        setZone(newZone);
      }
    }

    dragStart.current = null;
    dragPosRef.current = null;
    setDragPos(null);
    setIsDragging(false);
    setDragState(null);
  }, [handleMove, storageKey]);

  const handlePointerDown = useCallback((e) => {
    if (!draggable) return;
    if (e.button !== undefined && e.button !== 0) return; // sólo botón principal
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    const targetBtn = e.target.closest('button');
    if (targetBtn && targetBtn !== e.currentTarget) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Se arrastra el elemento entero aunque el agarre sea un encabezado interno.
    const node = e.currentTarget;
    const rect = node.getBoundingClientRect();
    const current = getLayout()[storageKey];
    dragStart.current = {
      clientX,
      clientY,
      startLeft: current ? current.x : rect.left,
      startTop: current ? current.y : rect.top,
      moved: false,
    };
    wasDragged.current = false;

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);
    window.addEventListener('touchcancel', handleUp);
  }, [handleMove, handleUp, draggable, storageKey]);

  useEffect(() => () => {
    window.removeEventListener('mousemove', handleMove);
    window.removeEventListener('mouseup', handleUp);
    window.removeEventListener('touchmove', handleMove);
    window.removeEventListener('touchend', handleUp);
    window.removeEventListener('touchcancel', handleUp);
    if (dragState && dragState.key === storageKey) setDragState(null);
  }, [handleMove, handleUp, storageKey]);

  const handlers = draggable
    ? { onMouseDown: handlePointerDown, onTouchStart: handlePointerDown }
    : {};

  const resetPosition = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    setZone(remapZoneForViewport(defaultZone));
  }, [storageKey, defaultZone]);

  let pos;
  if (isDragging && dragPos) pos = dragPos;
  else if (typeof window === 'undefined') pos = { x: 0, y: 0 };
  else {
    pos = (attached && getLayout()[storageKey])
      || fallbackPosition(zone, sizeRef.current.w, sizeRef.current.h);
  }

  return { pos, zone, isDragging, wasDragged, handlers, elementRef, resetPosition };
}
